/**
 * Tokenizer for rendering chat message text in the inbox: clickable links +
 * WhatsApp-style inline formatting (*bold*, _italic_, ~strikethrough~), which
 * Meta delivers as raw markers in webhook text (e.g. its own system nudges).
 *
 * Pure and framework-free so it can be unit-tested; the React mapping lives in
 * the message bubble. No nesting — WhatsApp itself renders flat marks, and a
 * flat single pass can't blow up on adversarial input.
 */

export type MessageTextToken =
  | { kind: 'text'; text: string }
  | { kind: 'link'; href: string }
  | { kind: 'bold'; text: string }
  | { kind: 'italic'; text: string }
  | { kind: 'strike'; text: string }

const URL_RE = /https?:\/\/[^\s<>]+/g

// WhatsApp marks hug non-whitespace on both sides, never span lines, and sit on
// word boundaries — a marker glued to a letter/digit is literal text, so
// `phone_number_id` (or its Cyrillic equivalent, hence \p{L}) stays plain.
const INLINE_MARK_RE =
  /(?<![\p{L}\p{N}_*~])(\*([^\s*](?:[^*\n]*[^\s*])?)\*|_([^\s_](?:[^_\n]*[^\s_])?)_|~([^\s~](?:[^~\n]*[^\s~])?)~)(?![\p{L}\p{N}_*~])/gu

/** Trailing sentence punctuation is prose, not part of the URL. */
function splitTrailingPunctuation(url: string): [string, string] {
  const m = url.match(/[.,;:!?…]+$/)
  if (!m) return [url, '']
  return [url.slice(0, url.length - m[0].length), m[0]]
}

function tokenizeInlineMarks(text: string, out: MessageTextToken[]): void {
  let last = 0
  for (const m of text.matchAll(INLINE_MARK_RE)) {
    const idx = m.index ?? 0
    if (idx > last) out.push({ kind: 'text', text: text.slice(last, idx) })
    if (m[2] !== undefined) out.push({ kind: 'bold', text: m[2] })
    else if (m[3] !== undefined) out.push({ kind: 'italic', text: m[3] })
    else out.push({ kind: 'strike', text: m[4] })
    last = idx + m[0].length
  }
  if (last < text.length) out.push({ kind: 'text', text: text.slice(last) })
}

export function tokenizeMessageText(text: string): MessageTextToken[] {
  const out: MessageTextToken[] = []
  let last = 0
  for (const m of text.matchAll(URL_RE)) {
    const idx = m.index ?? 0
    if (idx > last) tokenizeInlineMarks(text.slice(last, idx), out)
    const [href, rest] = splitTrailingPunctuation(m[0])
    out.push({ kind: 'link', href })
    if (rest) out.push({ kind: 'text', text: rest })
    last = idx + m[0].length
  }
  if (last < text.length) tokenizeInlineMarks(text.slice(last), out)
  return out
}
